import React, { useState } from 'react';
import { Box } from '@biome-design-system/ui/Box';
import { useIntl, type MessageDescriptor } from '@kyruus/intl';
import { Autocomplete } from '@biome-design-system/ui/Autocomplete';
import { IconButton } from '@biome-design-system/ui/IconButton';
import { SearchIcon } from '@biome-design-system/icons/SearchIcon';

import useSuggestions from 'Src/hooks/useSuggestions';
import { messages } from '../../messages';

export interface LocationInputProps {
  /** The location text shown in the input / read-only display. */
  displayLocation: string | undefined;
  /** Called when a suggestion is selected. */
  onChange: (selectedItem: string) => void;
  /** Called as the user types in the input. */
  onInputValueChange: (inputValue: string) => void;
  /** Called when the search is submitted. */
  onSubmit: () => void;
  /** Placeholder message descriptor for the input. */
  placeholder: MessageDescriptor;
  /** Selected search distance, used to build the input's aria label. */
  distance: string | number;
}

export const LocationInput = ({
  displayLocation,
  onChange,
  onInputValueChange,
  onSubmit,
  placeholder,
  distance
}: LocationInputProps) => {
  const intl = useIntl();

  // term used to fetch suggestions for
  const [inputSuggestionsTerm, setInputSuggestionsTerm] = useState<
    string | undefined
  >(undefined);

  // auto-suggest suggestions
  const { suggestions, suggestionsLoading } = useSuggestions({
    term: inputSuggestionsTerm
  });

  return (
    <Box id="location-inputwrapper" display="flex" flexDirection="column">
      <Autocomplete
        id="location-filter-input"
        freeSolo={true}
        disableClearable={true}
        size="small"
        fullWidth={true}
        label={intl.formatMessage(messages.locationInputLabel)}
        options={suggestions}
        loading={suggestionsLoading}
        loadingText={intl.formatMessage(messages.locationInputLoadingLabel)}
        noOptionsText={intl.formatMessage(messages.locationInputNoOptionsLabel)}
        inputValue={displayLocation ?? ''}
        onInputChange={(_event, value) => {
          setInputSuggestionsTerm(value);
          onInputValueChange(value);
        }}
        onChange={(_event, value) => {
          if (typeof value === 'string') {
            onChange(value);
          }
        }}
        placeholder={intl.formatMessage(placeholder)}
        slotProps={{
          htmlInput: {
            'aria-label': intl.formatMessage(
              messages.locationInputAriaLabelText,
              { distance: Number(distance) }
            ),
            autoComplete: 'off',
            // suppress password-manager autofill overlays (1Password, LastPass)
            'data-1p-ignore': true,
            'data-lpignore': true
          },
          inputLabel: { sx: { textIndent: 'unset' } }
        }}
        startAdornment={
          <IconButton
            aria-label={intl.formatMessage(messages.locationSearchButton)}
            data-testid="location-search-button"
            onClick={() => onSubmit()}
            size="small"
            title={intl.formatMessage(messages.locationSearchButton)}
          >
            <SearchIcon fontSize="small" />
          </IconButton>
        }
      />
    </Box>
  );
};
